import { useCallback, useEffect, useRef } from 'react'

/** Debounce for toggle network calls; optimistic UI stays immediate. */
export const TOGGLE_NETWORK_DEBOUNCE_MS = 400

type ScheduleSettledToggleArgs = {
  /** Optimistic desired boolean after the click. */
  desired: boolean
  /** Persist final state when debounce settles and desired !== baseline. */
  commit: (desired: boolean) => Promise<void>
  /** Called when commit throws; baseline is pre-burst server/synced value. */
  onError?: (baseline: boolean) => void
  /** Called when settle is a no-op (ended at baseline). */
  onSkip?: () => void
}

/**
 * Settled toggle: rapid clicks only send the final desired boolean after delayMs.
 * If the user returns to the pre-burst value, no network call is made.
 */
export function useSettledToggleNetwork(delayMs: number = TOGGLE_NETWORK_DEBOUNCE_MS) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const desiredRef = useRef<boolean | null>(null)
  const baselineRef = useRef<boolean | null>(null)
  const commitRef = useRef<((desired: boolean) => Promise<void>) | null>(null)
  const onErrorRef = useRef<((baseline: boolean) => void) | null>(null)
  const onSkipRef = useRef<(() => void) | null>(null)
  const generationRef = useRef(0)

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const schedule = useCallback(
    ({ desired, commit, onError, onSkip }: ScheduleSettledToggleArgs) => {
      if (baselineRef.current === null) {
        baselineRef.current = !desired
      }
      desiredRef.current = desired
      commitRef.current = commit
      onErrorRef.current = onError ?? null
      onSkipRef.current = onSkip ?? null

      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }

      const generation = ++generationRef.current
      timerRef.current = setTimeout(() => {
        void (async () => {
          if (generation !== generationRef.current) {
            return
          }

          timerRef.current = null
          const nextDesired = desiredRef.current
          const baseline = baselineRef.current
          const commitFn = commitRef.current
          const errorFn = onErrorRef.current
          const skipFn = onSkipRef.current

          desiredRef.current = null
          baselineRef.current = null
          commitRef.current = null
          onErrorRef.current = null
          onSkipRef.current = null

          if (nextDesired === null || baseline === null || !commitFn) {
            return
          }

          if (nextDesired === baseline) {
            skipFn?.()
            return
          }

          try {
            await commitFn(nextDesired)
          } catch {
            errorFn?.(baseline)
          }
        })()
      }, delayMs)
    },
    [delayMs],
  )

  return { schedule }
}
